/**
 * Creates a callback URL for authentication redirects
 * Since Supabase uses hash fragments for redirects, we'll just use a simple callback URL
 * and rely on localStorage for return path tracking
 */
export function createAuthCallbackUrl(returnTo?: string): string {
  // Ensure this base path matches your actual callback route
  const baseCallbackPath = '/auth/callback'; 
  const callbackUrl = new URL(baseCallbackPath, window.location.origin);

  if (returnTo && returnTo !== '/') {
    // Add the final destination as a query parameter
    callbackUrl.searchParams.set('returnTo', returnTo);
  }

  return callbackUrl.toString(); 
}

/**
 * Saves the current path to localStorage and redirects to login page
 * Used when a protected route is accessed by an unauthenticated user
 * @param currentPath - The current page path to return to after auth
 * @param viewMode - Whether to show 'login' or 'signup' view (default: 'login')
 */
export function savePathAndRedirectToLogin(currentPath: string, viewMode: 'login' | 'signup' = 'login'): void {
  // Save the current path to localStorage for post-login redirect
  if (currentPath && currentPath !== '/' && currentPath !== '/login') {
    // Strip any hash or query parameters to get a clean path
    const cleanPath = currentPath.split('?')[0].split('#')[0];
    
    // Store both the path and a timestamp to protect against race conditions
    const pathData = {
      returnPath: cleanPath,
      timestamp: new Date().toISOString()
    };
    
    // Store in both formats for backward compatibility
    localStorage.setItem('authReturnPath', cleanPath);
    localStorage.setItem('authReturnPathData', JSON.stringify(pathData));
    
  }
  
  // Redirect to login page with view mode parameter
  const viewParam = viewMode === 'signup' ? '?view=signup' : '';
  window.location.href = `/login${viewParam}`;
}

// Track if the path has been accessed within this page load
let pathAccessedTimestamp: number | null = null;
const PATH_REUSE_TIMEOUT_MS = 1000; // 1 second timeout for path reuse

/**
 * Gets the return path from localStorage and clears it
 * Used after successful authentication to redirect the user back
 * Uses a time-based lock to allow multiple callers to access the same path
 * within a short time window, addressing race conditions in auth callbacks
 */
export function getAndClearReturnPath(): string {
  // Try to get the path data first
  const pathDataString = localStorage.getItem('authReturnPathData');
  const legacyPath = localStorage.getItem('authReturnPath');
  
  // Current timestamp for age calculations
  const now = Date.now();
  
  // If path was recently accessed, return it without clearing
  // This prevents race conditions between multiple auth callback handlers
  if (pathAccessedTimestamp && (now - pathAccessedTimestamp) < PATH_REUSE_TIMEOUT_MS) {
    // Use path data if available, fallback to legacy path
    if (pathDataString) {
      try {
        const pathData = JSON.parse(pathDataString);
        return pathData.returnPath || '/';
      } catch {
        return legacyPath || '/';
      }
    }
    return legacyPath || '/';
  }
  
  // Set the access timestamp
  pathAccessedTimestamp = now;
  
  let returnPath = '/';
  
  // Try to parse the path data
  if (pathDataString) {
    try {
      const pathData = JSON.parse(pathDataString);
      returnPath = pathData.returnPath || '/';
    } catch {
      // Fallback to legacy path if JSON parsing fails
      returnPath = legacyPath || '/';
    }
  } else {
    // Use legacy path if no path data
    returnPath = legacyPath || '/';
  }
  
  // Clear both path storage mechanisms after a longer delay (3 seconds)
  // This gives plenty of time for the navigation to complete before clearing
  setTimeout(() => {
    localStorage.removeItem('authReturnPath');
    localStorage.removeItem('authReturnPathData');
  }, 3000); // Increased from 1000ms to 3000ms
  
  return returnPath;
} 
